import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';
import { environment } from '@environment/environment';
import {
    MissingTranslationHandler,
    MissingTranslationHandlerParams,
    TranslateLoader,
    TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

/**
 * Factory function for loading translation JSON files
 * Located in `assets/i18n/` and suffixed with `.json`
 */
export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * Custom missing translation handler for development feedback.
 * Returns a warning string in dev mode or just the key in production.
 */
export class MissingTranslationService implements MissingTranslationHandler {
    handle(params: MissingTranslationHandlerParams): string {
        const { key, translateService } = params;

        return getFallbackMessage(key, translateService.currentLang);
    }
}

function getFallbackMessage(key: string, currentLanguage: string): string {
    return environment.production ? key : `Translation missing for key '${key}' in '${currentLanguage}' locale.`;
}

/** ngx-translate setup and language switching tied to user profile. */
@Injectable({
    providedIn: 'root',
})
export class LocalizationService {
    constructor(
        private readonly translateService: TranslateService,
        private readonly authApiService: AuthApiService
    ) {}

    /** Subscribes to auth user changes and syncs the active locale. */
    init(): void {
        this.subscribeToUser();
    }

    private subscribeToUser(): void {
        this.authApiService.user$.subscribe((user) => {
            if (user) {
                this.switchLanguage(user.language || this.browserLanguage);
                return;
            }

            // Signed out (also after a logout): back to the browser language, not the last user's.
            this.switchLanguage(this.browserLanguage);
        });
    }

    /**
     * Gets the browser language or falls back to default if not supported
     */
    get browserLanguage(): string {
        const browserLang = this.translateService.getBrowserLang();

        return browserLang && environment.localization.supportedLanguages.includes(browserLang)
            ? browserLang
            : environment.localization.defaultLanguage;
    }

    /**
     * Returns the current application language
     */
    get currentLanguage(): string {
        return this.translateService.currentLang;
    }

    /**
     * Changes the current application language
     * @param lang - Language code to switch to
     */
    switchLanguage(lang: string): void {
        if (environment.localization.supportedLanguages.includes(lang)) {
            this.translateService.use(lang);
        }
    }

    /**
     * Returns translated text for a key synchronously.
     * @param key - Translation key
     * @param interpolateParams - Optional placeholder values
     * @returns Translated string or a dev-only missing-key message
     */
    getTranslation(key: string, interpolateParams?: object): string {
        return this.translateService.instant(key, interpolateParams) ?? getFallbackMessage(key, this.currentLanguage);
    }
}
